/**
 * Dev preview only (REACT_APP_LENDING_FIXTURE=<name> npm start): a small in-memory stand-in for the gateway that
 * walks an application through the journey with realistic timings, so every screen can be built and reviewed
 * without a live session. Never used in a build without that variable.
 */
import { ILendingCustomer, ILendingSession, ILoanOffer } from './types';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const base = (): ILendingSession => ({
  applicationReference: 'LND261024K7QM2XRA',
  status: 'INFORMATION_REQUIRED',
  purchaseAmount: 130_000_000,
  currency: 'NGN',
  productDescription: '5kVA solar inverter kit',
  journey: [
    'CUSTOMER_INFORMATION', 'BVN_COLLECTION', 'BVN_VERIFICATION', 'NIN_COLLECTION', 'NIN_VERIFICATION', 'DATE_OF_BIRTH',
    'EMAIL_ADDRESS', 'PHONE_NUMBER', 'BANK_STATEMENT_UPLOAD', 'BANK_STATEMENT_ANALYSIS', 'ELIGIBILITY_ASSESSMENT', 'LOAN_OFFER',
    'LOAN_ACCEPTANCE', 'EQUITY_CONTRIBUTION', 'PAYMENT_STATUS', 'REPAYMENT_ACCOUNT_DETAILS',
  ],
  fieldRequirements: [
    { field: 'FIRST_NAME', requirement: 'REQUIRED' },
    { field: 'LAST_NAME', requirement: 'REQUIRED' },
    { field: 'DATE_OF_BIRTH', requirement: 'REQUIRED' },
    { field: 'EMAIL_ADDRESS', requirement: 'REQUIRED' },
    { field: 'PHONE_NUMBER', requirement: 'REQUIRED' },
    { field: 'BVN', requirement: 'REQUIRED', verificationRequired: true },
    { field: 'NIN', requirement: 'REQUIRED', verificationRequired: true },
    { field: 'ADDRESS', requirement: 'OPTIONAL' },
    { field: 'BANK_STATEMENT', requirement: 'REQUIRED' },
  ],
  missingFields: ['FIRST_NAME', 'LAST_NAME', 'DATE_OF_BIRTH', 'EMAIL_ADDRESS', 'PHONE_NUMBER', 'BVN', 'NIN'],
  customer: {},
  statement: { status: 'NOT_SUBMITTED' },
});

const customer: ILendingCustomer = {
  firstName: 'Ada', lastName: 'Obi', email: 'ada.obi@example.com', phoneNumber: '08012345678', dateOfBirth: '1990-05-14',
  bvn: '*******1234', nin: '*******5678', address: '12 Admiralty Way, Lekki, Lagos',
};

const OFFER: ILoanOffer = {
  offerId: 'offer-1', status: 'PRESENTED', currency: 'NGN', eligibleAmount: 160_000_000, purchaseAmount: 130_000_000,
  equityAmount: 50_000_000, financedAmount: 80_000_000, tenorUnit: 'MONTHS', repaymentFrequency: 'MONTHLY',
  expiresAt: '2026-10-25T12:00:00',
  options: [
    { tenor: 3, numberOfRepayments: 3, instalmentAmount: 28_400_000, totalInterest: 7_000_000, totalFees: 0, totalRepayable: 87_000_000, firstRepaymentDate: '2026-11-24', maturityDate: '2027-01-24' },
    { tenor: 6, numberOfRepayments: 6, instalmentAmount: 15_666_700, totalInterest: 14_000_000, totalFees: 0, totalRepayable: 94_000_000, firstRepaymentDate: '2026-11-24', maturityDate: '2027-04-24' },
    { tenor: 12, numberOfRepayments: 12, instalmentAmount: 8_666_700, totalInterest: 24_000_000, totalFees: 0, totalRepayable: 104_000_000, firstRepaymentDate: '2026-11-24', maturityDate: '2027-10-24' },
  ],
};

const START: Record<string, Partial<ILendingSession>> = {
  start: {},
  statement: { status: 'VERIFICATION_PENDING', customer, missingFields: [] },
  statementFailed: { status: 'STATEMENT_ANALYSIS_FAILED', customer, missingFields: [], statement: { status: 'FAILED', failureReason: 'The statement covers fewer than 6 months.' } },
  assessing: { status: 'STATEMENT_ANALYSIS_PENDING', customer, missingFields: [], statement: { status: 'ANALYSING', fileName: 'Demo_doc_testing.pdf' } },
  offer: { status: 'OFFER_PRESENTED', customer, missingFields: [], statement: { status: 'ANALYSED' }, eligibility: { outcome: 'ELIGIBLE' } },
  ineligible: { status: 'INELIGIBLE', customer, missingFields: [], eligibility: { outcome: 'INELIGIBLE', reasonCode: 'NOT_ELIGIBLE', message: 'You are not eligible for financing on this purchase.' } },
  tooLarge: { status: 'INELIGIBLE', customer, missingFields: [], eligibility: { outcome: 'INELIGIBLE', reasonCode: 'PURCHASE_ABOVE_ELIGIBLE_AMOUNT', message: 'This purchase is more than you can finance. Choose a different product.', maxFinancedAmount: 80_000_000, maxPurchaseAmount: 100_000_000 } },
  failed: { status: 'VERIFICATION_FAILED', customer, missingFields: [] },
  funded: { status: 'FUNDED', customer, missingFields: [], repaymentAccount: { accountNumber: '9012345678', accountName: 'CFN/Ada Obi', bankName: 'VFD Microfinance Bank' } },
};

/** The starting point: ?fixture=<name> in the dev URL, else REACT_APP_LENDING_FIXTURE (e.g. start, offer, tooLarge). */
const startAt = () => {
  const fromUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('fixture') : null;
  return START[fromUrl ?? process.env.REACT_APP_LENDING_FIXTURE ?? 'start'] ?? {};
};

let session: ILendingSession = { ...base(), ...startAt() };
let offer: ILoanOffer = { ...OFFER };
let changedAt = Date.now();

const move = (next: Partial<ILendingSession>) => {
  session = { ...session, ...next };
  changedAt = Date.now();
};

const equity = (status: 'DUE' | 'FAILED' | 'PAID') => ({
  publicKey: 'pk_test_cfn_fixture', status, amount: offer.equityAmount, currency: 'NGN',
  metadata: { lendingApplicationReference: session.applicationReference },
});

/** Time moves the application on, as the backend's checks and jobs would. */
const advance = () => {
  const elapsed = Date.now() - changedAt;
  if (session.status === 'VERIFICATION_PENDING' && session.statement?.status === 'ANALYSING' && elapsed > 3000) {
    move({ status: 'STATEMENT_ANALYSIS_PENDING' });
  } else if (session.status === 'STATEMENT_ANALYSIS_PENDING' && elapsed > 5000) {
    move({ status: 'ASSESSMENT_PENDING', statement: { ...session.statement!, status: 'ANALYSED' } });
  } else if (session.status === 'ASSESSMENT_PENDING' && elapsed > 4000) {
    move({ status: 'ELIGIBLE', eligibility: { outcome: 'ELIGIBLE' } });
  } else if (session.status === 'EQUITY_PAYMENT_PENDING' && elapsed > 4000) {
    move({ status: 'EQUITY_PAYMENT_SUCCESSFUL', equityPayment: equity('PAID') });
  } else if (session.status === 'EQUITY_PAYMENT_SUCCESSFUL' && elapsed > 3000) {
    move({ status: 'PROCESSING' });
  } else if (session.status === 'PROCESSING' && elapsed > 5000) {
    move({ status: 'FUNDED', repaymentAccount: { accountNumber: '9012345678', accountName: 'CFN/Ada Obi', bankName: 'VFD Microfinance Bank' } });
  }
};

export const fixtureApi = {
  getSession: async () => {
    await delay(400);
    advance();
    return session;
  },
  updateCustomer: async (update: ILendingCustomer) => {
    await delay(700);
    move({ status: 'INITIATED', customer: { ...session.customer, ...update }, missingFields: [] });
    return session;
  },
  submitStatement: async (statement: { bankCode: string; contentBase64: string; fileName?: string }) => {
    await delay(1200);
    move({ status: 'VERIFICATION_PENDING', statement: { status: 'ANALYSING', bankCode: statement.bankCode, fileName: statement.fileName, submittedAt: new Date().toISOString() } });
    return session;
  },
  getOffer: async () => {
    await delay(500);
    if (session.status === 'ELIGIBLE') {
      move({ status: 'OFFER_PRESENTED' });
    }
    return offer;
  },
  acceptOffer: async (offerId: string, tenor: number) => {
    await delay(700);
    offer = { ...offer, status: 'ACCEPTED', selectedTenor: tenor };
    move({ status: 'OFFER_ACCEPTED', equityPayment: equity('DUE') });
    return offer;
  },
  rejectOffer: async () => {
    await delay(700);
    offer = { ...offer, status: 'REJECTED' };
    move({ status: 'OFFER_REJECTED' });
    return offer;
  },
  /** Stands in for the payment SDK: the customer pays, then the gateway confirms a few seconds later. */
  pay: async (onPaid: (reference: string) => void) => {
    await delay(1000);
    move({ status: 'EQUITY_PAYMENT_PENDING' });
    onPaid('PRF 7173-0193-2391');
  },
};

export const fixtureMode = () => !!process.env.REACT_APP_LENDING_FIXTURE;
